import React, { memo, useMemo, useCallback } from 'react'
import { Form, Alert, Checkbox } from 'antd'
import { extract } from '../../../../../_utils/object'
import { canShowField, sortKeywordsForDisplay } from '../helpers'
import { PageAnalyzerResultsProps } from '../types'
import SuggestedField from './suggested-field'
import { KeywordCheckbox } from './keyword-checkbox'
import { KeywordsSection } from './keywords-section'

/**
 * PageAnalyzerResults component
 * Handles page analyzer specific UI and logic including:
 * - Form-based field selection interface
 * - Keyword selection state management
 * - Integration with SuggestedField components
 */
const PageAnalyzerResults = ({
    domain,
    currentVariantData,
    form,
    selectedKeywords,
    setSelectedKeywords,
}: PageAnalyzerResultsProps) => {
    // Extract suggested data
    const suggestedImage = useMemo(() => {
        return currentVariantData?.compositeImage
            ? extract(currentVariantData.compositeImage, 'url')
            : currentVariantData?.imageUrl
    }, [currentVariantData?.compositeImage, currentVariantData?.imageUrl])

    const suggestedKws = useMemo(() => {
        const keywords = currentVariantData ? extract(currentVariantData, 'keywords', []) : []
        return Array.isArray(keywords) ? keywords : []
    }, [currentVariantData?.keywords])

    // Sort keywords for display using column-major layout
    const sortedKeywords = useMemo(() => {
        return sortKeywordsForDisplay(suggestedKws)
    }, [suggestedKws])

    // Check if suggestions are empty
    const suggestionsAreEmpty = !currentVariantData || Object.values(currentVariantData).every((v) => v === null)

    // Field availability checks
    const hasTitleSuggestion = !!currentVariantData?.title
    const hasBodySuggestion = !!currentVariantData?.body
    const hasImageSuggestion = suggestedImage !== null
    const hasKwSuggestions = suggestedKws.length > 0

    const canShowTitle =
        !suggestionsAreEmpty && hasTitleSuggestion && canShowField(domain, 'title', 'content_suggestions')
    const canShowBody = !suggestionsAreEmpty && hasBodySuggestion && canShowField(domain, 'body', 'content_suggestions')
    const canShowImage =
        !suggestionsAreEmpty && hasImageSuggestion && canShowField(domain, 'imageUrl', 'content_suggestions')
    const canShowKw =
        !suggestionsAreEmpty && hasKwSuggestions && canShowField(domain, 'keywords', 'content_suggestions')

    // Keyword selection state - memoized for performance
    const keywordSelectionState = useMemo(() => {
        const totalSuggestedKws = suggestedKws.length
        const totalSelectedKws = selectedKeywords?.length ?? 0
        const allKwSelection = hasKwSuggestions && totalSelectedKws === totalSuggestedKws
        const partialKwSelection = hasKwSuggestions && !allKwSelection && totalSelectedKws > 0

        return {
            totalSuggestedKws,
            totalSelectedKws,
            allKwSelection,
            partialKwSelection,
        }
    }, [hasKwSuggestions, suggestedKws.length, selectedKeywords?.length])

    // Memoized keyword checkbox change handler
    const handleKeywordCheckboxChange = useCallback(
        ({ target: { checked } }: { target: { checked: boolean } }) => {
            setSelectedKeywords(checked ? suggestedKws : [])
        },
        [suggestedKws, setSelectedKeywords],
    )

    // Memoized keyword group change handler
    const handleKeywordGroupChange = useCallback(
        (nextKeywords: string[]) => {
            setSelectedKeywords(nextKeywords)
            form.setFieldsValue({
                keywords: nextKeywords.length === keywordSelectionState.totalSuggestedKws,
            })
        },
        [form, keywordSelectionState.totalSuggestedKws, setSelectedKeywords],
    )

    return (
        <>
            <Alert
                type={suggestionsAreEmpty ? 'warning' : 'success'}
                showIcon={true}
                message={
                    suggestionsAreEmpty ? (
                        <span>We were unable to find any suggestions for the provided Landing URL.</span>
                    ) : (
                        <span>
                            We've found the following suggestions for the provided Landing URL. Review and select which
                            fields you'd like to use.
                        </span>
                    )
                }
            />

            {!suggestionsAreEmpty && (
                <Form form={form} size="small" style={{ marginTop: 16 }}>
                    {canShowTitle && (
                        <SuggestedField
                            label="Title"
                            field="title"
                            source={currentVariantData!}
                            form={form}
                            initialValue={true}
                            contentClickable={true}
                        />
                    )}

                    {canShowBody && (
                        <SuggestedField
                            label="Body"
                            field="body"
                            source={currentVariantData!}
                            form={form}
                            initialValue={true}
                            contentClickable={true}
                        />
                    )}

                    {canShowImage && (
                        <SuggestedField
                            label="Image"
                            field="imageUrl"
                            source={currentVariantData!}
                            form={form}
                            initialValue={true}
                            contentClickable={true}
                        >
                            <div className="suggested-image-wrapper">
                                <div
                                    className="suggested-image"
                                    style={{ backgroundImage: `url('${suggestedImage}')` }}
                                />
                            </div>
                        </SuggestedField>
                    )}

                    {canShowKw && (
                        <div className="suggestion-section">
                            <KeywordsSection
                                form={form}
                                keywords={suggestedKws}
                                selectedKeywords={selectedKeywords}
                                onKeywordChange={(keyword, checked) => {
                                    const currentSelected = selectedKeywords ?? []
                                    const nextKeywords = checked
                                        ? [...currentSelected, keyword]
                                        : currentSelected.filter((k: string) => k !== keyword)
                                    handleKeywordGroupChange(nextKeywords)
                                }}
                                onMainCheckboxChange={handleKeywordCheckboxChange}
                                title="Keywords"
                                className="kw-suggestions"
                                allSelected={keywordSelectionState.allKwSelection}
                                partialSelected={keywordSelectionState.partialKwSelection}
                            />
                        </div>
                    )}
                </Form>
            )}
        </>
    )
}

PageAnalyzerResults.displayName = 'PageAnalyzerResults'

export default PageAnalyzerResults
